/**
 * Home page (marketing landing) copy — merged into translations[lang].home.landing
 * so t('home.landing.*') works like the rest of the app.
 */
export const HOME_PAGE_LANDING = {
  en: {
    recommended: 'Recommended',
    yes: 'Enabled',
    no: 'No',
    pricing: {
      historyCaption: 'days of history',
      vpd: 'VPD',
    },
    demo: {
      strainName: 'Purple Haze #3',
      presetLabel: 'Preset:',
      cycleDayLabel: 'Cycle day:',
      tempOptimal: 'Optimal',
      humNormal: 'Normal',
      tempDelta: '↑ 0.3°',
      humDelta: '↓ 2%',
      co2Unit: 'ppm',
      roomTitle: 'Growing Space A1',
      active: 'Active',
      plants: 'Plants',
      plantsCount: '24 plants',
      light: 'Light',
      lightOn: 'On • 18/6',
      ventilation: 'Ventilation',
      ventilationValue: 'Auto • 65%',
      irrigation: 'Irrigation',
      irrigationIn: 'in 4 h',
      controllerTitle: 'Controller #1',
      sensors: 'Sensors',
      outletsLine: '4 on',
    },
    modules: {
      strainsBullets: ['Genetics & lineage', 'Cycle history', 'Grow presets'],
      roomsBullets: ['Live climate', 'Equipment control', 'Smart alerts'],
      analyticsBullets: ['Event diary', 'Tasks & reminders', 'Cycle analytics'],
    },
    flow: {
      title: 'How the system fits together',
      subtitle:
        'Shefa Green links strain, cuttings, growing space, controllers, and diary into one clear chain.',
      stepLabel: (n) => `Step ${n}`,
      step1Title: 'Strain',
      step1Desc: 'Genetics, history, growth stages',
      step2Title: 'Cuttings',
      step2Desc: 'Propagation, QR, provenance',
      step3Title: 'Growing Space',
      step3Desc: 'Climate, water, outlets, equipment',
      step4Title: 'Controllers',
      step4Desc: 'Data, control, status',
      step5Title: 'Diary & analytics',
      step5Desc: 'Notes, tasks, decisions',
    },
    controllers: {
      para1:
        'Shefa Green controllers connect to climate and water sensors and switch outlets for your equipment.',
      para2:
        'Everything from controllers lands in one system next to strains, growing spaces, and grower tasks.',
      feat1Title: 'Live monitoring',
      feat1Body: 'Temperature, humidity, CO₂, EC, pH, and ORP — all online in one place.',
      feat2Title: 'Equipment control',
      feat2Body: 'Run ventilation, cooling, dehumidification, and irrigation through outlets.',
      feat3Title: 'Diary integration',
      feat3Body: 'Events are recorded in growing space and strain history.',
      summary:
        'Climate and water stay in one workspace with growing spaces, strains, diary, and tasks.',
    },
    panel: {
      controllerLabel: 'Controller',
      deviceName: 'ShefaGreen #1',
      climate: 'Climate',
      equipment: 'Equipment',
      water: 'Water',
      outlets: 'Outlets',
      cooling: 'Cooling',
      dehumidifying: 'Dehumidifying',
      on: 'On',
      off: 'Off',
      flow: 'Flow',
      flowUnit: 'L/min',
      mainLight: 'Main lights',
      airConditioner: 'Air conditioning',
      irrigationPump: 'Irrigation pump',
      dehumidifier: 'Dehumidifier',
    },
    ai: {
      sectionTitle: 'Future AI assistant for growers',
      heading: 'Structured data for smarter analysis',
      body1:
        'Shefa Green captures strains, growing spaces, climate, water, controllers, diary, tasks, cycles, and outcomes in a structured model.',
      body2:
        'With enough history, a future AI layer can help analyze cycles, spot patterns, flag deviations, compare results, and suggest what to check next.',
      b1Title: 'AI helps you read the data',
      b1Body: 'The intelligence layer finds patterns that are hard to see manually.',
      b2Title: 'Growers stay in charge',
      b2Body: 'AI may suggest actions — every change still needs grower confirmation.',
      b3Title: 'Transparent recommendations',
      b3Body: 'The system explains which data led to a suggestion and what to verify.',
      importantLead: 'Important:',
      importantBody:
        'The AI assistant will not run the facility by itself. It is a decision-support tool for professional growers.',
      cardAssistant: 'AI Grower Assistant',
      cardCycleTitle: 'Cycle analysis: Purple Haze #3',
      insight: 'Insight',
      i1Title: 'Climate is stable',
      i1Body: 'Temperature and CO₂ stayed in range for the last 7 days.',
      i2Title: 'Humidity often spikes at night',
      i2Body: '12 readings above 65% between 22:00–06:00.',
      i3Title: 'Review dehumidification & night scenario',
      i3Body: 'Recommendation based on 42 days of the current cycle.',
      confirm: 'Recommendation requires grower confirmation.',
      more: 'Details',
      s1Title: 'Cycle history',
      s1Body: 'Compare results across cycles for the same strain.',
      s2Title: 'Patterns & deviations',
      s2Body: 'Surface anomalies in climate and growth.',
      s3Title: 'Decision support',
      s3Body: 'Hints on what to check next from your data.',
    },
    cta: {
      chipClimate: 'Climate on track',
      chipOutlets: 'Outlets active',
      chipSecure: 'Data protected',
    },
    mini: {
      strain: 'Strain',
      room: 'Growing Space',
      controller: 'Controller',
      diary: 'Diary',
      diaryEntries: (n) => `${n} entries`,
    },
    footer: {
      copyright: '© 2026 Shefa Green',
      telegram: 'Telegram bot',
    },
  },
  ru: {
    recommended: 'Рекомендуем',
    yes: 'включён',
    no: 'нет',
    pricing: {
      historyCaption: 'истории',
      vpd: 'VPD',
    },
    demo: {
      strainName: 'Purple Haze #3',
      presetLabel: 'Пресет:',
      cycleDayLabel: 'День цикла:',
      tempOptimal: 'Оптимально',
      humNormal: 'Норма',
      tempDelta: '↑ 0,3°',
      humDelta: '↓ 2%',
      co2Unit: 'ч/млн',
      roomTitle: 'Зона выращивания A1',
      active: 'Активна',
      plants: 'Растений',
      plantsCount: '24 шт',
      light: 'Свет',
      lightOn: 'Вкл • 18/6',
      ventilation: 'Вентиляция',
      ventilationValue: 'Авто • 65%',
      irrigation: 'Полив',
      irrigationIn: 'через 4 ч',
      controllerTitle: 'Контроллер #1',
      sensors: 'Датчиков',
      outletsLine: '4 включены',
    },
    modules: {
      strainsBullets: ['Генетика и потомство', 'История циклов', 'Пресеты выращивания'],
      roomsBullets: ['Климат в реальном времени', 'Управление оборудованием', 'Автоматические уведомления'],
      analyticsBullets: ['Дневник событий', 'Задачи и напоминания', 'Аналитика циклов'],
    },
    flow: {
      title: 'Как устроена система',
      subtitle:
        'Shefa Green связывает сорт, черенки, зону выращивания, контроллеры и дневник в одну понятную цепочку.',
      stepLabel: (n) => `Шаг ${n}`,
      step1Title: 'Сорт',
      step1Desc: 'Генетика, история, стадии роста',
      step2Title: 'Черенки',
      step2Desc: 'Размножение, QR, происхождение',
      step3Title: 'Зона выращивания',
      step3Desc: 'Климат, вода, розетки, оборудование',
      step4Title: 'Контроллеры',
      step4Desc: 'Данные, управление, статусы',
      step5Title: 'Дневник и аналитика',
      step5Desc: 'Заметки, задачи, решения',
    },
    controllers: {
      para1:
        'Контроллеры Shefa Green подключаются к датчикам климата, воды и управляют розетками для оборудования.',
      para2:
        'Вся информация с контроллеров попадает в единую систему и отображается рядом с сортами, зонами выращивания и задачами гроувера.',
      feat1Title: 'Мониторинг в реальном времени',
      feat1Body: 'Температура, влажность, CO₂, EC, pH и ORP — всё видите онлайн.',
      feat2Title: 'Управление оборудованием',
      feat2Body: 'Включайте вентиляцию, охлаждение, осушение и орошение через розетки.',
      feat3Title: 'Интеграция с дневником',
      feat3Body: 'Все события записываются в историю зоны выращивания и сорта.',
      summary:
        'Климат и вода отображаются в одном рабочем пространстве, рядом с зонами выращивания, сортами, дневником и задачами.',
    },
    panel: {
      controllerLabel: 'Контроллер',
      deviceName: 'ShefaGreen #1',
      climate: 'Климат',
      equipment: 'Оборудование',
      water: 'Вода',
      outlets: 'Розетки',
      cooling: 'Охлаждение',
      dehumidifying: 'Осушение',
      on: 'Вкл',
      off: 'Выкл',
      flow: 'Поток',
      flowUnit: 'л/м',
      mainLight: 'Свет основной',
      airConditioner: 'Кондиционер',
      irrigationPump: 'Насос полива',
      dehumidifier: 'Осушитель',
    },
    ai: {
      sectionTitle: 'Будущий AI-ассистент для гроувера',
      heading: 'Структурированные данные для умного анализа',
      body1:
        'Shefa Green собирает информацию о сортах, зонах выращивания, климате, воде, контроллерах, дневнике, задачах, циклах и результатах в структурированном формате.',
      body2:
        'На основе накопленной истории будущий AI-ассистент сможет помочь гроуверу анализировать циклы, находить паттерны, выделять отклонения, сравнивать результаты и подсказывать, что проверить дальше.',
      b1Title: 'AI помогает понимать данные',
      b1Body: 'Интеллектуальный слой анализирует историю и выявляет закономерности, которые сложно заметить вручную.',
      b2Title: 'Гроувер принимает решения',
      b2Body: 'AI предлагает возможные действия, но все изменения требуют подтверждения гроувера.',
      b3Title: 'Прозрачность рекомендаций',
      b3Body: 'Система объясняет, на основе каких данных сделан вывод и что нужно проверить.',
      importantLead: 'Важно:',
      importantBody:
        'AI-ассистент не управляет объектом самостоятельно. Это инструмент поддержки решений для профессионального гроувера.',
      cardAssistant: 'AI-ассистент гроувера',
      cardCycleTitle: 'Анализ цикла: Purple Haze #3',
      insight: 'Инсайт',
      i1Title: 'Климат стабилен',
      i1Body: 'Температура и CO₂ в пределах нормы последние 7 дней.',
      i2Title: 'Влажность чаще выходит за норму ночью',
      i2Body: 'Обнаружено 12 случаев превышения 65% в период 22:00–06:00.',
      i3Title: 'Проверьте осушение и ночной сценарий',
      i3Body: 'Рекомендация на основе анализа 42 дней текущего цикла.',
      confirm: 'Рекомендация требует подтверждения гроувера.',
      more: 'Подробнее',
      s1Title: 'История циклов',
      s1Body: 'Сравнение результатов разных циклов одного сорта.',
      s2Title: 'Паттерны и отклонения',
      s2Body: 'Выявление аномалий в климате и росте.',
      s3Title: 'Поддержка решений',
      s3Body: 'Подсказки, что проверить на основе данных.',
    },
    cta: {
      chipClimate: 'Климат в норме',
      chipOutlets: 'Розетки активны',
      chipSecure: 'Данные защищены',
    },
    mini: {
      strain: 'Сорт',
      room: 'Зона выращивания',
      controller: 'Контроллер',
      diary: 'Дневник',
      diaryEntries: (n) => `${n} записей`,
    },
    footer: {
      copyright: '© 2026 Shefa Green',
      telegram: 'Telegram-бот',
    },
  },
  he: {
    recommended: 'מומלץ',
    yes: 'מופעל',
    no: 'לא',
    pricing: {
      historyCaption: 'היסטוריה',
      vpd: 'VPD',
    },
    demo: {
      strainName: 'Purple Haze #3',
      presetLabel: 'פריסט:',
      cycleDayLabel: 'יום במחזור:',
      tempOptimal: 'אופטימלי',
      humNormal: 'תקין',
      tempDelta: '↑ 0.3°',
      humDelta: '↓ 2%',
      co2Unit: 'ppm',
      roomTitle: 'חלל גידול A1',
      active: 'פעיל',
      plants: 'צמחים',
      plantsCount: '24 יח׳',
      light: 'תאורה',
      lightOn: 'דלוק • 18/6',
      ventilation: 'אוורור',
      ventilationValue: 'אוטו • 65%',
      irrigation: 'השקיה',
      irrigationIn: 'בעוד 4 ש׳',
      controllerTitle: 'בקר #1',
      sensors: 'חיישנים',
      outletsLine: '4 דלוקים',
    },
    modules: {
      strainsBullets: ['גנטיקה וצאצאים', 'היסטוריית מחזורים', 'פריסטים לגידול'],
      roomsBullets: ['אקלים בזמן אמת', 'בקרת ציוד', 'התראות חכמות'],
      analyticsBullets: ['יומן אירועים', 'משימות ותזכורות', 'אנליטיקת מחזורים'],
    },
    flow: {
      title: 'איך המערכת מתחברת',
      subtitle: 'Shefa Green מקשר זן, ייחורים, חלל גידול, בקרים ויומן לשרשרת אחת ברורה.',
      stepLabel: (n) => `שלב ${n}`,
      step1Title: 'זן',
      step1Desc: 'גנטיקה, היסטוריה, שלבי גידול',
      step2Title: 'ייחורים',
      step2Desc: 'השתרה, QR, מקור',
      step3Title: 'חלל גידול',
      step3Desc: 'אקלים, מים, שקעים, ציוד',
      step4Title: 'בקרים',
      step4Desc: 'נתונים, שליטה, סטטוס',
      step5Title: 'יומן ואנליטיקה',
      step5Desc: 'הערות, משימות, החלטות',
    },
    controllers: {
      para1: 'בקרי Shefa Green מתחברים לחיישני אקלים ומים ומפעילים שקעים לציוד.',
      para2: 'כל המידע מהבקרים נכנס למערכת אחת ליד זנים, חללי גידול ומשימות.',
      feat1Title: 'ניטור חי',
      feat1Body: 'טמפרטורה, לחות, CO₂, EC, pH ו-ORP — הכל במקום אחד.',
      feat2Title: 'בקרת ציוד',
      feat2Body: 'אוורור, קירור, ייבוש והשקיה דרך השקעים.',
      feat3Title: 'שילוב עם יומן',
      feat3Body: 'אירועים נרשמים בהיסטוריית החלל גידול והזן.',
      summary: 'אקלים ומים באותו workspace עם חללי גידול, זנים, יומן ומשימות.',
    },
    panel: {
      controllerLabel: 'בקר',
      deviceName: 'ShefaGreen #1',
      climate: 'אקלים',
      equipment: 'ציוד',
      water: 'מים',
      outlets: 'שקעים',
      cooling: 'קירור',
      dehumidifying: 'ייבוש',
      on: 'דלוק',
      off: 'כבוי',
      flow: 'זרימה',
      flowUnit: 'ל׳/דק׳',
      mainLight: 'תאורה ראשית',
      airConditioner: 'מזגן',
      irrigationPump: 'משאבת השקיה',
      dehumidifier: 'מייבש לחות',
    },
    ai: {
      sectionTitle: 'עוזר AI עתידי לגדלים',
      heading: 'נתונים מובנים לניתוח חכם',
      body1: 'Shefa Green אוסף זנים, חללי גידול, אקלים, מים, בקרים, יומן, משימות, מחזורים ותוצאות במבנה אחיד.',
      body2: 'עם מספיק היסטוריה, שכבת AI עתידית יכולה לעזור לנתח מחזורים, למצוא דפוסים ולהציע מה לבדוק הלאה.',
      b1Title: 'AI עוזר לקרוא נתונים',
      b1Body: 'שכבת המודיעין מוצאת דפוסים שקשה לראות ידנית.',
      b2Title: 'הגדל מחליט',
      b2Body: 'AI עשוי להציע פעולות — כל שינוי דורש אישור הגדל.',
      b3Title: 'שקיפות בהמלצות',
      b3Body: 'המערכת מסבירה אילו נתונים הובילו להמלצה ומה לוודא.',
      importantLead: 'חשוב:',
      importantBody: 'עוזר ה-AI לא מפעיל את המתקן לבד. זה כלי תמיכה להחלטות מקצועיות.',
      cardAssistant: 'עוזר AI לגדל',
      cardCycleTitle: 'ניתוח מחזור: Purple Haze #3',
      insight: 'תובנה',
      i1Title: 'האקלים יציב',
      i1Body: 'טמפרטורה ו-CO₂ בטווח 7 הימים האחרונים.',
      i2Title: 'לחות עולה בלילה',
      i2Body: '12 קריאות מעל 65% בין 22:00–06:00.',
      i3Title: 'בדקו ייבוש ותרחיש לילה',
      i3Body: 'המלצה על סמך 42 ימי המחזור הנוכחי.',
      confirm: 'ההמלצה דורשת אישור הגדל.',
      more: 'פרטים',
      s1Title: 'היסטוריית מחזורים',
      s1Body: 'השוואת תוצאות בין מחזורים לאותו זן.',
      s2Title: 'דפוסים וחריגים',
      s2Body: 'זיהוי חריגות באקלים ובצמיחה.',
      s3Title: 'תמיכה בהחלטות',
      s3Body: 'רמזים מה לבדוק לפי הנתונים.',
    },
    cta: {
      chipClimate: 'אקלים תקין',
      chipOutlets: 'שקעים פעילים',
      chipSecure: 'נתונים מוגנים',
    },
    mini: {
      strain: 'זן',
      room: 'חלל גידול',
      controller: 'בקר',
      diary: 'יומן',
      diaryEntries: (n) => `${n} רשומות`,
    },
    footer: {
      copyright: '© 2026 Shefa Green',
      telegram: 'בוט טלגרם',
    },
  },
  ua: {
    recommended: 'Рекомендовано',
    yes: 'увімкнено',
    no: 'ні',
    pricing: {
      historyCaption: 'історії',
      vpd: 'VPD',
    },
    demo: {
      strainName: 'Purple Haze #3',
      presetLabel: 'Пресет:',
      cycleDayLabel: 'День циклу:',
      tempOptimal: 'Оптимально',
      humNormal: 'Норма',
      tempDelta: '↑ 0,3°',
      humDelta: '↓ 2%',
      co2Unit: 'ppm',
      roomTitle: 'Зона вирощування A1',
      active: 'Активна',
      plants: 'Рослин',
      plantsCount: '24 шт',
      light: 'Світло',
      lightOn: 'Увімк. • 18/6',
      ventilation: 'Вентиляція',
      ventilationValue: 'Авто • 65%',
      irrigation: 'Полив',
      irrigationIn: 'через 4 год',
      controllerTitle: 'Контролер #1',
      sensors: 'Датчиків',
      outletsLine: '4 увімкнено',
    },
    modules: {
      strainsBullets: ['Генетика та нащадки', 'Історія циклів', 'Пресети вирощування'],
      roomsBullets: ['Клімат у реальному часі', 'Керування обладнанням', 'Автоматичні сповіщення'],
      analyticsBullets: ['Щоденник подій', 'Задачі та нагадування', 'Аналітика циклів'],
    },
    flow: {
      title: 'Як побудована система',
      subtitle:
        'Shefa Green пов’язує сорт, живці, простір вирощування, контролери та щоденник в один зрозумілий ланцюг.',
      stepLabel: (n) => `Крок ${n}`,
      step1Title: 'Сорт',
      step1Desc: 'Генетика, історія, стадії росту',
      step2Title: 'Черенки',
      step2Desc: 'Розмноження, QR, походження',
      step3Title: 'Зона вирощування',
      step3Desc: 'Клімат, вода, розетки, обладнання',
      step4Title: 'Контролери',
      step4Desc: 'Дані, керування, статуси',
      step5Title: 'Щоденник і аналітика',
      step5Desc: 'Нотатки, задачі, рішення',
    },
    controllers: {
      para1:
        'Контролери Shefa Green підключаються до датчиків клімату та води й керують розетками обладнання.',
      para2:
        'Уся інформація з контролерів потрапляє в одну систему поруч із сортами, просторами вирощування та задачами гровера.',
      feat1Title: 'Моніторинг у реальному часі',
      feat1Body: 'Температура, вологість, CO₂, EC, pH та ORP — усе онлайн.',
      feat2Title: 'Керування обладнанням',
      feat2Body: 'Вентиляція, охолодження, осушення та зрошення через розетки.',
      feat3Title: 'Інтеграція з щоденником',
      feat3Body: 'Події записуються в історію зони вирощування та сорту.',
      summary:
        'Клімат і вода в одному робочому просторі з просторами вирощування, сортами, щоденником і задачами.',
    },
    panel: {
      controllerLabel: 'Контролер',
      deviceName: 'ShefaGreen #1',
      climate: 'Клімат',
      equipment: 'Обладнання',
      water: 'Вода',
      outlets: 'Розетки',
      cooling: 'Охолодження',
      dehumidifying: 'Осушення',
      on: 'Увімк.',
      off: 'Вимк.',
      flow: 'Потік',
      flowUnit: 'л/хв',
      mainLight: 'Основне світло',
      airConditioner: 'Кондиціонер',
      irrigationPump: 'Насос поливу',
      dehumidifier: 'Осушувач',
    },
    ai: {
      sectionTitle: 'Майбутній AI-асистент для гровера',
      heading: 'Структуровані дані для розумного аналізу',
      body1:
        'Shefa Green збирає сорти, зони вирощування, клімат, воду, контролери, щоденник, задачі, цикли та результати в структурованому вигляді.',
      body2:
        'На основі історії майбутній AI зможе допомагати аналізувати цикли, знаходити патерни й підказувати, що перевірити далі.',
      b1Title: 'AI допомагає читати дані',
      b1Body: 'Інтелектуальний шар знаходить закономірності, які важко помітити вручну.',
      b2Title: 'Рішення за гровером',
      b2Body: 'AI може пропонувати дії — але зміни потребують підтвердження гровера.',
      b3Title: 'Прозорість рекомендацій',
      b3Body: 'Система пояснює, на основі яких даних зроблено висновок.',
      importantLead: 'Важливо:',
      importantBody:
        'AI-асистент не керує об’єктом самостійно. Це інструмент підтримки рішень.',
      cardAssistant: 'AI-асистент гровера',
      cardCycleTitle: 'Аналіз циклу: Purple Haze #3',
      insight: 'Інсайт',
      i1Title: 'Клімат стабільний',
      i1Body: 'Температура й CO₂ у нормі останні 7 днів.',
      i2Title: 'Вологість частіше вночі',
      i2Body: '12 випадків понад 65% між 22:00–06:00.',
      i3Title: 'Перевірте осушення й нічний сценарій',
      i3Body: 'Рекомендація на основі 42 днів поточного циклу.',
      confirm: 'Рекомендація потребує підтвердження гровера.',
      more: 'Деталі',
      s1Title: 'Історія циклів',
      s1Body: 'Порівняння результатів різних циклів одного сорту.',
      s2Title: 'Патерни та відхилення',
      s2Body: 'Виявлення аномалій у кліматі та рості.',
      s3Title: 'Підтримка рішень',
      s3Body: 'Підказки, що перевірити за даними.',
    },
    cta: {
      chipClimate: 'Клімат у нормі',
      chipOutlets: 'Розетки активні',
      chipSecure: 'Дані захищені',
    },
    mini: {
      strain: 'Сорт',
      room: 'Зона вирощування',
      controller: 'Контролер',
      diary: 'Щоденник',
      diaryEntries: (n) => `${n} записів`,
    },
    footer: {
      copyright: '© 2026 Shefa Green',
      telegram: 'Telegram-бот',
    },
  },
  es: {
    recommended: 'Recomendado',
    yes: 'Sí',
    no: 'No',
    pricing: {
      historyCaption: 'de historial',
      vpd: 'VPD',
    },
    demo: {
      strainName: 'Purple Haze #3',
      presetLabel: 'Preset:',
      cycleDayLabel: 'Día de ciclo:',
      tempOptimal: 'Óptimo',
      humNormal: 'Normal',
      tempDelta: '↑ 0.3°',
      humDelta: '↓ 2%',
      co2Unit: 'ppm',
      roomTitle: 'Espacio de cultivo A1',
      active: 'Activa',
      plants: 'Plantas',
      plantsCount: '24 uds.',
      light: 'Luz',
      lightOn: 'Enc. • 18/6',
      ventilation: 'Ventilación',
      ventilationValue: 'Auto • 65%',
      irrigation: 'Riego',
      irrigationIn: 'en 4 h',
      controllerTitle: 'Controlador #1',
      sensors: 'Sensores',
      outletsLine: '4 encendidos',
    },
    modules: {
      strainsBullets: ['Genética y linaje', 'Historial de ciclos', 'Presets de cultivo'],
      roomsBullets: ['Clima en vivo', 'Control de equipos', 'Alertas automáticas'],
      analyticsBullets: ['Diario de eventos', 'Tareas y recordatorios', 'Analítica de ciclos'],
    },
    flow: {
      title: 'Cómo encaja el sistema',
      subtitle:
        'Shefa Green enlaza cepa, esquejes, espacio de cultivo, controladores y diario en una cadena clara.',
      stepLabel: (n) => `Paso ${n}`,
      step1Title: 'Cepa',
      step1Desc: 'Genética, historial, etapas',
      step2Title: 'Esquejes',
      step2Desc: 'Propagación, QR, procedencia',
      step3Title: 'Espacio de cultivo',
      step3Desc: 'Clima, agua, enchufes, equipos',
      step4Title: 'Controladores',
      step4Desc: 'Datos, control, estado',
      step5Title: 'Diario y analítica',
      step5Desc: 'Notas, tareas, decisiones',
    },
    controllers: {
      para1:
        'Los controladores Shefa Green se conectan a sensores de clima y agua y accionan enchufes del equipo.',
      para2:
        'Toda la información llega a un solo sistema junto a cepas, espacios de cultivo y tareas del cultivador.',
      feat1Title: 'Monitoreo en vivo',
      feat1Body: 'Temperatura, humedad, CO₂, EC, pH y ORP en un solo lugar.',
      feat2Title: 'Control de equipos',
      feat2Body: 'Ventilación, refrigeración, deshumidificación y riego vía enchufes.',
      feat3Title: 'Integración con el diario',
      feat3Body: 'Los eventos quedan en el historial de espacio de cultivo y cepa.',
      summary:
        'Clima y agua en un mismo espacio de trabajo con espacios de cultivo, cepas, diario y tareas.',
    },
    panel: {
      controllerLabel: 'Controlador',
      deviceName: 'ShefaGreen #1',
      climate: 'Clima',
      equipment: 'Equipos',
      water: 'Agua',
      outlets: 'Enchufes',
      cooling: 'Refrigeración',
      dehumidifying: 'Deshumidificación',
      on: 'Enc.',
      off: 'Apag.',
      flow: 'Caudal',
      flowUnit: 'L/min',
      mainLight: 'Luz principal',
      airConditioner: 'Aire acondicionado',
      irrigationPump: 'Bomba de riego',
      dehumidifier: 'Deshumidificador',
    },
    ai: {
      sectionTitle: 'Futuro asistente IA para el cultivador',
      heading: 'Datos estructurados para un análisis más inteligente',
      body1:
        'Shefa Green captura cepas, espacios de cultivo, clima, agua, controladores, diario, tareas, ciclos y resultados en un modelo estructurado.',
      body2:
        'Con historial suficiente, una capa de IA podrá ayudar a analizar ciclos, detectar patrones y sugerir qué revisar.',
      b1Title: 'La IA ayuda a leer los datos',
      b1Body: 'Encuentra patrones difíciles de ver a mano.',
      b2Title: 'El cultivador decide',
      b2Body: 'La IA puede sugerir acciones; cada cambio requiere confirmación.',
      b3Title: 'Recomendaciones transparentes',
      b3Body: 'El sistema explica en qué datos se basa cada sugerencia.',
      importantLead: 'Importante:',
      importantBody:
        'El asistente de IA no operará la instalación solo. Es apoyo a decisiones profesionales.',
      cardAssistant: 'Asistente IA del cultivador',
      cardCycleTitle: 'Análisis de ciclo: Purple Haze #3',
      insight: 'Insight',
      i1Title: 'Clima estable',
      i1Body: 'Temperatura y CO₂ en rango los últimos 7 días.',
      i2Title: 'Humedad alta de noche',
      i2Body: '12 lecturas por encima del 65% entre 22:00–06:00.',
      i3Title: 'Revise deshumidificación y escenario nocturno',
      i3Body: 'Recomendación basada en 42 días del ciclo actual.',
      confirm: 'La recomendación requiere confirmación del cultivador.',
      more: 'Más',
      s1Title: 'Historial de ciclos',
      s1Body: 'Compare resultados entre ciclos de la misma cepa.',
      s2Title: 'Patrones y desvíos',
      s2Body: 'Detecte anomalías en clima y crecimiento.',
      s3Title: 'Apoyo a decisiones',
      s3Body: 'Pistas sobre qué revisar según sus datos.',
    },
    cta: {
      chipClimate: 'Clima en rango',
      chipOutlets: 'Enchufes activos',
      chipSecure: 'Datos protegidos',
    },
    mini: {
      strain: 'Cepa',
      room: 'Espacio de cultivo',
      controller: 'Controlador',
      diary: 'Diario',
      diaryEntries: (n) => `${n} entradas`,
    },
    footer: {
      copyright: '© 2026 Shefa Green',
      telegram: 'Bot de Telegram',
    },
  },
};
